import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FaPlus, FaEdit, FaPowerOff, FaChartBar } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import AtivarEventoModal from './components/AtivarEventoModal';
import RelatorioEventoModal from './components/RelatorioEventoModal';
import { listarEventosRsvp } from '~/Services/Service';
import { ROUTE_EVENTOS_RSVP } from '~/constant';
import AuthStorage from '~/Utils/AuthStorage';

const USUARIO_PODE_DESATIVAR = 1418;

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const EventoRsvpList = () => {
  const history = useHistory();
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [modalAtivar, setModalAtivar] = useState({
    show: false,
    evento: null,
    eventoAtivo: null,
  });
  const [modalRelatorio, setModalRelatorio] = useState({
    show: false,
    evento: null,
  });

  const carregarEventos = () => {
    setLoading(true);
    listarEventosRsvp()
      .then((res) => {
        const lista = res && res.eventos ? res.eventos : [];
        setEventos(lista);
      })
      .catch((err) => {
        const msg = extrairMensagemErro(err, 'Erro ao carregar eventos.');
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    carregarEventos();
  }, [reloadTick]);

  const eventoAtivo = useMemo(
    () => eventos.find((e) => e.ativo) || null,
    [eventos]
  );

  const eventosOrdenados = useMemo(() => {
    return [...eventos]
      .sort((a, b) => new Date(b.data_inicio_evento) - new Date(a.data_inicio_evento))
      .map((e) => ({
        ...e,
        id: e.codigo,
        data_inicio_evento_display: moment(e.data_inicio_evento).format('DD/MM/YYYY'),
        data_fim_confirmacao_display: moment(e.data_fim_confirmacao).format('DD/MM/YYYY'),
      }));
  }, [eventos]);

  const handleNovo = () => {
    history.push(`${ROUTE_EVENTOS_RSVP}/novo`);
  };

  const handleEditar = (item) => {
    history.push(`${ROUTE_EVENTOS_RSVP}/${item.codigo}/editar`);
  };

  const handleAtivar = (item) => {
    setModalAtivar({
      show: true,
      evento: item,
      eventoAtivo,
    });
  };

  const handleFecharModalAtivar = (sucesso) => {
    setModalAtivar({ show: false, evento: null, eventoAtivo: null });
    if (sucesso) {
      toast.success('Evento atualizado.');
      setReloadTick((t) => t + 1);
    }
  };

  const colunas = [
    { key: 'codigo', title: 'Código', ordenacao: false },
    { key: 'nome_evento', title: 'Nome', ordenacao: false },
    { key: 'cidade_uf', title: 'Cidade/UF', ordenacao: false },
    { key: 'data_inicio_evento_display', title: 'Data do Evento', ordenacao: false },
    { key: 'data_fim_confirmacao_display', title: 'Fim Confirmações', ordenacao: false },
    {
      key: 'ativo',
      title: 'Ativo',
      ordenacao: false,
      align: 'center',
      render: (val) =>
        val ? (
          <Badge variant="success">Sim</Badge>
        ) : (
          <Badge variant="secondary">Não</Badge>
        ),
    },
  ];

  const handleVerRelatorio = (item) => {
    setModalRelatorio({ show: true, evento: item });
  };

  const handleFecharModalRelatorio = () => {
    setModalRelatorio({ show: false, evento: null });
  };

  const acoes = [
    {
      label: 'Ver relatório',
      icon: <FaChartBar />,
      onClick: (item) => handleVerRelatorio(item),
      type: 'info',
    },
    {
      label: 'Editar',
      icon: <FaEdit />,
      onClick: (item) => handleEditar(item),
    },
    {
      label: 'Ativar',
      icon: <FaPowerOff />,
      onClick: (item) => handleAtivar(item),
      type: 'success',
      hidden: (item) => item.ativo,
    },
    {
      label: 'Desativar',
      icon: <FaPowerOff />,
      onClick: (item) => handleAtivar(item),
      style: { backgroundColor: '#ffc107', color: '#212529' },
      hidden: (item) => {
        if (!item.ativo) return true;
        const user = AuthStorage.getUser();
        return !user || user.usuario !== USUARIO_PODE_DESATIVAR;
      },
    },
  ];

  return (
    <Container fluid>
      <Header
        titulo="Eventos RSVP"
        subtitulo="Gerenciamento de eventos de confirmação de presença"
      />

      <Card className="mt-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Lista de Eventos</h5>
            <Button variant="primary" size="sm" onClick={handleNovo}>
              <FaPlus className="mr-1" /> Novo Evento
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Carregando...</p>
            </div>
          ) : eventosOrdenados.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>Nenhum evento cadastrado.</p>
              <Button variant="primary" size="sm" onClick={handleNovo}>
                <FaPlus className="mr-1" /> Criar primeiro evento
              </Button>
            </div>
          ) : (
            <Tabela
              colunas={colunas}
              dados={eventosOrdenados}
              action
              acoes={acoes}
              striped
              hover
            />
          )}
        </Card.Body>
      </Card>

      {modalAtivar.show && (
        <AtivarEventoModal
          evento={modalAtivar.evento}
          eventoAtivo={modalAtivar.eventoAtivo}
          onClose={handleFecharModalAtivar}
        />
      )}

      {modalRelatorio.show && (
        <RelatorioEventoModal
          evento={modalRelatorio.evento}
          onClose={handleFecharModalRelatorio}
        />
      )}
    </Container>
  );
};

export default EventoRsvpList;
